import { assert, CheckedAddress, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { shuffle } from 'remeda'
import { useConfig } from 'wagmi'

import { DelegateDialogContainerProps } from '../DelegateDialogContentContainer'
import { delegateQueryOptions } from './delegateQuery'
import { getChainConfigEntry } from '@/config/chain'
import { useCloseDialog } from '@/domain/state/dialogs/hooks'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'
import { predefinedDelegates as _predefinedDelegates } from '@/features/spk/governance/config'

// @note: randomize the order of predefined delegates to avoid bias
const predefinedDelegates = shuffle(_predefinedDelegates)

export interface UseDelegateDialogResult {
  delegatedTo: Delegate | undefined
  isDelegatingToNotWhitelisted: boolean
  isSelectedDelegateAlreadySet: boolean
  staked?: NormalizedNumber
  spk: Token
  delegates: Delegate[]
  actionsViewState: ActionsViewState
  pageStatus: PageStatus
  onSkip: () => void
  skipText: string
}

export interface Delegate {
  address: CheckedAddress
  avatar?: string
  name: string
  isSelected: boolean
  isEnablingPointsBoost: boolean
  isCurrentDelegate: boolean
  onClick: () => void
}

export function useDelegateDialog(params: DelegateDialogContainerProps): UseDelegateDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const wagmiConfig = useConfig()
  const closeDialog = useCloseDialog()
  const { account: delegatorAddress, chainId } = useConnectedAddress()
  const sparkTokenConfig = getChainConfigEntry(chainId).spk
  assert(sparkTokenConfig, 'Spark token config not available for this chain')

  const tokenRepository = useSpkTokenRepository({ chainId, account: delegatorAddress })
  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))
  const delegator = { address: delegatorAddress, name: 'No delegation' }

  const { delegate: currentDelegate } = useSuspenseQuery(
    delegateQueryOptions({ delegator: delegatorAddress, chainId, wagmiConfig }),
  ).data

  const [selectedDelegate, setSelectedDelegate] = useState<CheckedAddress>(() => {
    if (params.action === 'undelegate') {
      return delegatorAddress
    }
    const predefinedDelegate = predefinedDelegates.find(({ address }) => address === currentDelegate)
    if (predefinedDelegate) {
      return predefinedDelegate.address
    }
    return predefinedDelegates[0]!.address
  })

  const filteredPredefinedDelegates = predefinedDelegates.filter(({ address }) => address !== delegator.address)
  const delegates = [...filteredPredefinedDelegates, delegator].map((delegate) => ({
    ...delegate,
    isSelected: selectedDelegate === delegate.address,
    isEnablingPointsBoost: delegate.address !== delegator.address,
    isCurrentDelegate: delegate.address === currentDelegate && delegate.address !== delegator.address,
    onClick: () => setSelectedDelegate(delegate.address),
  }))

  const isDelegatingToNotWhitelisted = Boolean(
    currentDelegate && !predefinedDelegates.some(({ address }) => address === currentDelegate),
  )

  const objectives = createObjectives({ selectedDelegate, currentDelegate, delegator: delegator.address })
  const isSelectedDelegateAlreadySet = objectives.length === 0

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: params.onSuccess ?? (() => setPageStatus('success')),
    enabled: true,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when selected delegate changes
  useEffect(() => {
    resetActions()
  }, [selectedDelegate, resetActions])

  function onSkip(): void {
    if (params.onSuccess) {
      params.onSuccess()
      return
    }

    if (params.variant === 'after-stake') {
      setPageStatus('success')
    } else {
      closeDialog()
    }
  }

  return {
    spk,
    staked: params.variant === 'after-stake' ? params.staked : undefined,
    delegatedTo:
      selectedDelegate === delegatorAddress
        ? undefined
        : delegates.find((delegate) => delegate.address === selectedDelegate),
    delegates,
    isDelegatingToNotWhitelisted,
    isSelectedDelegateAlreadySet,
    actionsViewState,
    onSkip,
    skipText: params.variant === 'after-stake' ? 'Skip' : 'Close',
    pageStatus: {
      state: pageStatus,
      actionsEnabled: true,
      goToSuccessScreen: params.onSuccess ?? (() => setPageStatus('success')),
    },
  }
}

interface CreateObjectivesParams {
  selectedDelegate: CheckedAddress
  currentDelegate: CheckedAddress | undefined
  delegator: CheckedAddress
}

function createObjectives({ selectedDelegate, currentDelegate, delegator }: CreateObjectivesParams): Objective[] {
  if (selectedDelegate === currentDelegate) {
    return []
  }
  if (selectedDelegate === delegator && !currentDelegate) {
    return []
  }
  if (selectedDelegate === delegator) {
    return [{ type: 'undelegate' as const, delegator }]
  }
  return [{ type: 'delegate' as const, delegate: selectedDelegate, delegator }]
}
